/**
 * QuestionSetList Component
 * Requirements: 7.1, 7.7, 7.8 - List with publish filter, date sort, CRUD actions
 * Requirement 49.8: CSV import button
 */
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import type { QuestionSetListItem, PublishStatusFilter, SortOrder } from '../types/questionSet.types';

interface QuestionSetListProps {
  questionSets: QuestionSetListItem[];
  isLoading: boolean;
  onEdit: (questionSet: QuestionSetListItem) => void;
  onDelete: (questionSet: QuestionSetListItem) => void;
  onTogglePublish: (id: string, isPublished: boolean) => void;
  onCreate: () => void;
  onImport?: () => void;
}

export function QuestionSetList({
  questionSets,
  isLoading,
  onEdit,
  onDelete,
  onTogglePublish,
  onCreate,
  onImport,
}: QuestionSetListProps) {
  const [statusFilter, setStatusFilter] = useState<PublishStatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleFilterChange = useCallback((event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as PublishStatusFilter);
  }, []);

  const handleSortClick = useCallback(() => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  const filteredAndSortedQuestionSets = useMemo(() => {
    let filtered = questionSets;

    // Apply status filter
    if (statusFilter === 'published') {
      filtered = filtered.filter((qs) => qs.isPublished);
    } else if (statusFilter === 'unpublished') {
      filtered = filtered.filter((qs) => !qs.isPublished);
    }

    // Sort by creation date
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [questionSets, statusFilter, sortOrder]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          質問セット管理
        </Typography>
        <Box display="flex" gap={1}>
          {onImport && (
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={onImport}>
              CSVインポート
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
            新規作成
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">公開状態</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter}
            label="公開状態"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">すべて</MenuItem>
            <MenuItem value="published">公開中のみ</MenuItem>
            <MenuItem value="unpublished">非公開のみ</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredAndSortedQuestionSets.length === 0 ? (
        <Alert severity="info">
          {statusFilter === 'all'
            ? '質問セットが登録されていません。'
            : statusFilter === 'published'
              ? '公開中の質問セットがありません。'
              : '非公開の質問セットがありません。'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="質問セット一覧">
            <TableHead>
              <TableRow>
                <TableCell>タイトル</TableCell>
                <TableCell>説明</TableCell>
                <TableCell>質問数</TableCell>
                <TableCell>公開状態</TableCell>
                <TableCell sortDirection={sortOrder}>
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortClick}
                  >
                    作成日
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedQuestionSets.map((questionSet) => (
                <TableRow key={questionSet.id}>
                  <TableCell>{questionSet.title}</TableCell>
                  <TableCell>
                    {questionSet.description
                      ? questionSet.description.length > 50
                        ? `${questionSet.description.substring(0, 50)}...`
                        : questionSet.description
                      : '-'}
                  </TableCell>
                  <TableCell>{questionSet.questionCount}</TableCell>
                  <TableCell>
                    <Chip
                      label={questionSet.isPublished ? '公開中' : '非公開'}
                      color={questionSet.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(questionSet.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={questionSet.isPublished ? '非公開にする' : '公開する'}
                      onClick={() =>
                        onTogglePublish(questionSet.id, !questionSet.isPublished)
                      }
                      size="small"
                      color={questionSet.isPublished ? 'primary' : 'default'}
                    >
                      {questionSet.isPublished ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="編集"
                      onClick={() => onEdit(questionSet)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="削除"
                      onClick={() => onDelete(questionSet)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
